import { Box, CSSObject, Icon, IconButton, List, styled, Theme, Toolbar } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import React, { useState } from 'react';
import sizeConfigs from '../../configs/sizeConfigs';
import ExpandCircleDownOutlinedIcon from '@mui/icons-material/ExpandCircleDownOutlined';
import SidebarItem from './SidebarItem';
import useStudentItems from './studentItems';
import useTeacherItems from './teacherItems';
import useAdminItems from './adminItems';
import { useSelector } from 'react-redux';
import { RootState } from '../../features/redux/store';
import { NavBarItems } from '../../features/authentication/types/user';

const drawerWidth = sizeConfigs.sidebar.width;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  // transition: theme.transitions.create("width", {
  //   easing: theme.transitions.easing.sharp,
  //   duration: theme.transitions.duration.enteringScreen,
  // }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  // transition: theme.transitions.create("width", {
  //   easing: theme.transitions.easing.sharp,
  //   duration: theme.transitions.duration.leavingScreen,
  // }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const Drawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  ...(open && {
    ...openedMixin(theme),
    '& .MuiDrawer-paper': openedMixin(theme),
  }),
  ...(!open && {
    ...closedMixin(theme),
    '& .MuiDrawer-paper': closedMixin(theme),
  }),
}));

export interface ISidebarProps {}

const Sidebar: React.FC<ISidebarProps> = () => {
  const role = useSelector((state: RootState) => state.user.role);
  const [open, setOpen] = useState(true);

  // Get items using hooks
  const studentItems = useStudentItems();
  const teacherItems = useTeacherItems();
  const adminItems = useAdminItems();

  const handleDrawerOpen = () => {
    setOpen(true);
  };

  const handleDrawerClose = () => {
    setOpen(false);
  };

  const openHelpdeskLink = () => {
    window.open('https://www.reklamaceskoly.cz/', '_blank', 'noopener,noreferrer');
  };

  function getItems(): NavBarItems[] {
    switch (role) {
      case 'STUDENT':
        return studentItems;
      case 'TEACHER':
        return teacherItems;
      case 'ADMIN':
      case 'SUPER_TEACHER':
        return adminItems;
      default:
        return studentItems;
    }
  }

  // Split items by alignTo (default to top when undefined)
  const items = getItems();
  const topItems = items.filter((item) => item.sidebarProps?.alignTo !== 'bottom');
  const bottomItems = items.filter((item) => item.sidebarProps?.alignTo === 'bottom');

  return (
    <Drawer variant="permanent" open={open}>
      <Toolbar />

      {/* Top sidebar items */}
      <Box sx={{ overflow: 'auto', position: 'relative' }}>
        <List disablePadding>
          {topItems.map((route, index) => (
            <SidebarItem open={open} item={route} key={index} />
          ))}
        </List>
      </Box>

      {/* Hide/open panel button */}
      {open ? (
        <IconButton onClick={handleDrawerClose}>
          <Icon style={{ transform: 'rotate(90deg)' }}>
            <ExpandCircleDownOutlinedIcon />
          </Icon>
        </IconButton>
      ) : (
        <IconButton onClick={handleDrawerOpen}>
          <Icon style={{ transform: 'rotate(275deg)' }}>
            <ExpandCircleDownOutlinedIcon />
          </Icon>
        </IconButton>
      )}

      {/* Bottom sidebar items */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'flex-end',
          position: 'absolute',
          bottom: '52px',
        }}
      >
        <List sx={{ width: '250px' }} disablePadding>
          {bottomItems.map((route, index) => (
            <SidebarItem open={open} item={route} key={index} />
          ))}
        </List>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
